import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import { Upload, Download, Plus, Trash2, Save } from 'lucide-react';
import { serviceService } from '../../services/api';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PricingManager = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [brandCategoryPrices, setBrandCategoryPrices] = useState([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const carBrands = [
    'Maruti Suzuki', 'Hyundai', 'Tata', 'Honda', 'Toyota',
    'Mahindra', 'Kia', 'Renault', 'Ford', 'Volkswagen',
    'Skoda', 'Nissan', 'MG', 'Jeep', 'BMW', 'Mercedes-Benz',
    'Audi', 'Volvo', 'Jaguar', 'Land Rover'
  ];

  const carCategories = [
    { value: 'Compact', label: 'Compact (Hatchback)', color: 'bg-green-100 text-green-800' },
    { value: 'Sedan', label: 'Sedan', color: 'bg-blue-100 text-blue-800' },
    { value: 'SUV', label: 'SUV/MUV', color: 'bg-purple-100 text-purple-800' },
    { value: 'Luxury', label: 'Luxury', color: 'bg-yellow-100 text-yellow-800' }
  ];

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const data = await serviceService.getAll();
      setServices(data.services || []);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast({
        title: 'Error',
        description: 'Failed to load services',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditPricing = (service, packageItem) => {
    setSelectedService(service);
    setSelectedPackage(packageItem);
    setBrandCategoryPrices(packageItem.brand_category_prices || []);
    setIsEditDialogOpen(true);
  };

  const handleAddBrandCategoryPrice = () => {
    setBrandCategoryPrices([...brandCategoryPrices, { brand: '', category: '', price: 0 }]);
  };

  const handleUpdateBrandCategoryPrice = (index, field, value) => {
    const updated = [...brandCategoryPrices];
    updated[index][field] = value;
    setBrandCategoryPrices(updated);
  };

  const handleDeleteBrandCategoryPrice = (index) => {
    const updated = brandCategoryPrices.filter((_, i) => i !== index);
    setBrandCategoryPrices(updated);
  };

  const handleSavePrices = async () => {
    try {
      // Validate that all entries have brand, category, and price
      const incomplete = brandCategoryPrices.some(p => !p.brand || !p.category || !p.price);
      if (incomplete) {
        toast({
          title: 'Validation Error',
          description: 'Please fill in brand, category, and price for all entries',
          variant: 'destructive'
        });
        return;
      }

      // Update the package with new brand+category prices
      const updatedPackages = selectedService.packages.map(pkg => {
        if (pkg.name === selectedPackage.name) {
          return { 
            ...pkg, 
            brand_category_prices: brandCategoryPrices
          };
        }
        return pkg;
      });

      // Call API to update service
      const response = await axios.patch(
        `${BACKEND_URL}/api/services/${selectedService.id}`,
        { packages: updatedPackages }
      );

      if (response.data.success) {
        toast({
          title: 'Success',
          description: 'Pricing updated successfully'
        });
        setIsEditDialogOpen(false);
        fetchServices();
      }
    } catch (error) {
      console.error('Error updating prices:', error);
      toast({
        title: 'Error',
        description: 'Failed to update prices',
        variant: 'destructive'
      });
    }
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/pricing/bulk-upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast({
          title: 'Success',
          description: `Updated ${response.data.updated_count} service packages. ${response.data.errors ? `Errors: ${response.data.errors.length}` : ''}`
        });
        
        if (response.data.errors && response.data.errors.length > 0) {
          console.error('Upload errors:', response.data.errors);
        }
        
        fetchServices();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to upload Excel file',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
      event.target.value = ''; // Reset file input
    }
  };

  const handleDownloadMasterData = async () => {
    try {
      setUploading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/pricing/master-data/download`,
        {
          responseType: 'blob'
        }
      );

      // Check if response is actually a blob
      if (response.data.type === 'application/json') {
        // Error response came as JSON
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Failed to download');
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'pricing_master_data.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Clean up
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Success',
        description: 'Master pricing data downloaded successfully'
      });
    } catch (error) {
      console.error('Error downloading master data:', error);
      let errorMessage = 'Failed to download master data';
      
      if (error.response?.data) {
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const errorData = JSON.parse(text);
            errorMessage = errorData.detail || errorMessage;
          } catch (e) {
            // Couldn't parse error
          }
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadMasterData = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/pricing/master-data/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast({
          title: 'Success',
          description: `Processed ${response.data.total_rows} rows. Updated ${response.data.updated_services} services. ${response.data.errors ? `Errors: ${response.data.total_errors}` : ''}`,
          duration: 5000
        });
        fetchServices();
      }
    } catch (error) {
      console.error('Error uploading master data:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to upload master data',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDownloadTemplate = () => {
    // Create instructions for template creation
    const instructions = `
PROPITSTOP PRICING TEMPLATE INSTRUCTIONS
==========================================

1. Create an Excel file (.xlsx)
2. Create ONE SHEET for each service package
3. Sheet name format: "ServiceName - PackageName"
   Example: "Periodic Services - Basic"

4. Each sheet should have 2 columns:
   Column A (Header): Car Brand
   Column B (Header): Price

5. Example data:
   Car Brand       | Price
   ----------------+-------
   Maruti Suzuki   | 2499
   Honda           | 2999
   Toyota          | 3499

6. Available Services and Packages:
${services.map(s => 
  s.packages.map(p => `   - ${s.name} - ${p.name} (Default: ₹${p.price})`).join('\n')
).join('\n')}

7. After creating the Excel file, upload it using the "Bulk Upload" button above.
    `.trim();

    const blob = new Blob([instructions], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'propitstop_pricing_template_instructions.txt';
    a.click();
    URL.revokeObjectURL(url);

    toast({
      title: 'Template Instructions Downloaded',
      description: 'Please follow the instructions to create your Excel template'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Brand-Specific Pricing Management</CardTitle>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={handleDownloadTemplate}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Template Instructions
              </Button>
              <label htmlFor="bulk-upload">
                <Button
                  variant="default"
                  className="flex items-center gap-2"
                  disabled={uploading}
                  asChild
                >
                  <span>
                    <Upload className="w-4 h-4" />
                    {uploading ? 'Uploading...' : 'Bulk Upload Excel'}
                  </span>
                </Button>
                <input
                  id="bulk-upload"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleBulkUpload}
                  className="hidden"
                />
              </label>
              
              <div className="border-l border-gray-300 mx-2"></div>
              
              <Button
                variant="default"
                onClick={handleDownloadMasterData}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                disabled={uploading}
              >
                <Download className="w-4 h-4" />
                Download Master Data
              </Button>
              
              <label htmlFor="master-upload">
                <Button
                  variant="default"
                  className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
                  disabled={uploading}
                  asChild
                >
                  <span>
                    <Upload className="w-4 h-4" />
                    Upload Master Data
                  </span>
                </Button>
                <input
                  id="master-upload"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleUploadMasterData}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Services List */}
      {services.map(service => (
        <Card key={service.id}>
          <CardHeader>
            <CardTitle className="text-xl">{service.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {service.packages.map((pkg, idx) => (
                <div key={idx} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-semibold text-lg">{pkg.name}</h3>
                      <p className="text-sm text-gray-600">
                        Duration: {pkg.duration}
                      </p>
                      <p className="text-sm text-gray-500">
                        {pkg.description}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {pkg.brand_category_prices?.length || 0} pricing rules configured
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleEditPricing(service, pkg)}
                    >
                      Edit Pricing
                    </Button>
                  </div>
                  
                  {pkg.brand_category_prices && pkg.brand_category_prices.length > 0 && (
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded p-3 mt-2">
                      <p className="text-xs font-medium text-gray-700 mb-2">Brand + Category Pricing:</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
                        {pkg.brand_category_prices.map((bcp, i) => (
                          <div key={i} className="bg-white p-2 rounded border shadow-sm">
                            <div className="font-medium text-blue-600">{bcp.brand}</div>
                            <div className="text-gray-600">{bcp.category}</div>
                            <div className="font-bold text-gray-900">₹{bcp.price}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Edit Brand + Category Pricing Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Edit Pricing: {selectedService?.name} - {selectedPackage?.name}
            </DialogTitle>
            <p className="text-sm text-gray-600 mt-2">
              Default Price: ₹{selectedPackage?.price} | Duration: {selectedPackage?.duration}
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-2">
              <p className="text-sm text-blue-900">
                <strong>How it works:</strong> Select a brand, then select a category (Compact/Sedan/SUV/Luxury), 
                and set a price. This price will apply to ALL cars of that brand in that category.
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Example: Maruti Suzuki + Compact + ₹2499 → applies to Alto, WagonR, Swift, etc.
              </p>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm font-medium">Brand + Category Pricing Rules:</p>
              <Button
                size="sm"
                onClick={handleAddBrandCategoryPrice}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Pricing Rule
              </Button>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Car Brand</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Price (₹)</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {brandCategoryPrices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                      <div className="flex flex-col items-center gap-2">
                        <p>No pricing rules configured yet.</p>
                        <p className="text-xs">Click "Add Pricing Rule" to set brand + category specific prices.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  brandCategoryPrices.map((bcp, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <select
                          value={bcp.brand}
                          onChange={(e) => handleUpdateBrandCategoryPrice(index, 'brand', e.target.value)}
                          className="w-full border rounded px-3 py-2"
                        >
                          <option value="">Select Brand</option>
                          {carBrands.map(brand => (
                            <option key={brand} value={brand}>{brand}</option>
                          ))}
                        </select>
                      </TableCell>
                      <TableCell>
                        <select
                          value={bcp.category}
                          onChange={(e) => handleUpdateBrandCategoryPrice(index, 'category', e.target.value)}
                          className="w-full border rounded px-3 py-2"
                        >
                          <option value="">Select Category</option>
                          {carCategories.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </select>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={bcp.price}
                          onChange={(e) => handleUpdateBrandCategoryPrice(index, 'price', parseFloat(e.target.value) || 0)}
                          className="w-full"
                          placeholder="Enter price"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteBrandCategoryPrice(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {brandCategoryPrices.length > 0 && (
              <div className="bg-gray-50 rounded p-4 border">
                <p className="text-xs font-medium text-gray-700 mb-2">Current Pricing Rules Summary:</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                  {brandCategoryPrices.map((bcp, i) => (
                    bcp.brand && bcp.category && (
                      <div key={i} className="bg-white p-2 rounded border">
                        <div className="font-medium text-blue-600">{bcp.brand}</div>
                        <div className="text-gray-600">{bcp.category}</div>
                        <div className="font-bold text-gray-900">₹{bcp.price || 0}</div>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSavePrices}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Pricing Rules
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PricingManager;
